import {
  DEFAULT_LOCALE,
  getMessages,
  type PasswordRequirementMessages,
} from "@/i18n";

/**
 * Per-rule password status for checklist UI (e.g. PasswordRequirements).
 * Form submission gates use validatePassword instead.
 */

export interface PasswordRequirementStatus {
  id: "minimumLength" | "mixedCase" | "hasNumber" | "specialCharactersAllowed";
  label: string;
  isMet: boolean;
  /** Informational lines stay grey and are excluded from validation. */
  isInformational?: boolean;
}

const MINIMUM_PASSWORD_LENGTH = 10;

// Mirror passwordValidationPattern lookaheads — ASCII classes only, not \p{Nd} or \d/u.
const HAS_LOWERCASE_LETTER = /[a-z]/;
const HAS_UPPERCASE_LETTER = /[A-Z]/;
const HAS_ASCII_DIGIT = /[0-9]/;

export function getPasswordRequirementStatus(
  password: string,
  labels: PasswordRequirementMessages = getMessages(DEFAULT_LOCALE)
    .passwordRequirements,
): PasswordRequirementStatus[] {
  const hasMinimumLength = password.length >= MINIMUM_PASSWORD_LENGTH;
  const hasLowercaseLetter = HAS_LOWERCASE_LETTER.test(password);
  const hasUppercaseLetter = HAS_UPPERCASE_LETTER.test(password);
  const hasNumber = HAS_ASCII_DIGIT.test(password);

  return [
    {
      id: "minimumLength",
      label: labels.minimumLength,
      isMet: hasMinimumLength,
    },
    {
      id: "mixedCase",
      label: labels.mixedCase,
      isMet: hasLowercaseLetter && hasUppercaseLetter,
    },
    {
      id: "hasNumber",
      label: labels.hasNumber,
      isMet: hasNumber,
    },
    {
      id: "specialCharactersAllowed",
      label: labels.specialCharactersAllowed,
      isMet: true,
      isInformational: true,
    },
  ];
}
